import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus } from "lucide-react";
import { Suspense, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useShowValidationErrors } from "~/common/hooks";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "~/components/ui";
import { PropertySchemaAssociationsList, usePropertySchemaAssociationsForm } from "~/features/admin/propertySchemas";
import { EvidencePropertySchemaTypes } from "~/features/dataImportProjects/types";
import {
  useDataSourceTemplateCreate,
  useDataSourceTemplateEdit,
  useDataSourceTemplateForEditForm,
} from "~/features/dataSourceTemplates/hooks";
import {
  type DataSourceTemplateFormData,
  zDataSourceTemplateFormData,
  zDataSourceTemplateFormDataKeys,
} from "~/features/dataSourceTemplates/types";

interface DataSourceTemplateFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this id. */
  dataSourceTemplateId?: string;
}

const FORM_ID = "data-source-template-form";

/** Property types selectable in the schema editor — the same set data sources offer. */
const SUPPORTED_PROPERTY_TYPES: string[] = [...EvidencePropertySchemaTypes];

const emptyDefaults: DataSourceTemplateFormData = {
  id: undefined,
  name: "",
  propertySchemaAssociations: [],
};

export function DataSourceTemplateFormDialog({
  open,
  onOpenChange,
  dataSourceTemplateId,
}: DataSourceTemplateFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(dataSourceTemplateId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit
              ? `${t("ui.edit")} ${t("model.dataSourceTemplate.modelName")}`
              : `${t("ui.add")} ${t("model.dataSourceTemplate.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<div className="py-6 text-center text-14-regular text-fg-3">…</div>}>
          {isEdit && dataSourceTemplateId ? (
            <EditBody dataSourceTemplateId={dataSourceTemplateId} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  return <FormBody defaults={emptyDefaults} prefilledAssociations={null} onClose={onClose} />;
}

function EditBody({ dataSourceTemplateId, onClose }: { dataSourceTemplateId: string; onClose: () => void }) {
  const { data } = useDataSourceTemplateForEditForm(dataSourceTemplateId);
  const template = data.dataSourceTemplates[0];
  if (!template) {
    return <div className="py-6 text-center text-14-regular text-fg-3">—</div>;
  }
  const defaults: DataSourceTemplateFormData = {
    id: template.id,
    name: template.name,
    propertySchemaAssociations: [],
  };
  const associations = (template.dataSourceTemplatePropertySchemaSet[0]?.propertySchemaSet.propertySchemaAssociations ??
    []) as Parameters<
    ReturnType<typeof usePropertySchemaAssociationsForm<DataSourceTemplateFormData>>["setDefaults"]
  >[0];
  return <FormBody defaults={defaults} prefilledAssociations={associations} onClose={onClose} />;
}

interface FormBodyProps {
  defaults: DataSourceTemplateFormData;
  /** Existing schemas to prefill on mount. `null` when creating. */
  prefilledAssociations:
    | Parameters<ReturnType<typeof usePropertySchemaAssociationsForm<DataSourceTemplateFormData>>["setDefaults"]>[0]
    | null;
  onClose: () => void;
}

function FormBody({ defaults, prefilledAssociations, onClose }: FormBodyProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(defaults.id);
  const methods = useForm<DataSourceTemplateFormData>({
    resolver: zodResolver(zDataSourceTemplateFormData),
    defaultValues: defaults,
  });
  const showValidationErrors = useShowValidationErrors(
    methods.setError,
    zDataSourceTemplateFormDataKeys,
    (message) => toast.error(message),
    ["propertySchemaAssociations"],
  );

  const schemas = usePropertySchemaAssociationsForm<DataSourceTemplateFormData>(methods.control);

  // Prefill schemas on mount (edit mode).
  useEffect(() => {
    if (prefilledAssociations) {
      schemas.setDefaults(prefilledAssociations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.id]);

  const { submit: create, processing: creating } = useDataSourceTemplateCreate({
    onError: (err) => showValidationErrors(err.fields ?? []),
    onSuccess: () => {
      methods.reset();
      onClose();
    },
  });
  const { submit: edit, processing: editing } = useDataSourceTemplateEdit({
    onError: (err) => showValidationErrors(err.fields ?? []),
    onSuccess: () => {
      methods.reset();
      onClose();
    },
  });
  const processing = creating || editing;

  const onSubmit = (data: DataSourceTemplateFormData) => {
    if (isEdit) {
      edit(data);
    } else {
      create(data);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="max-h-[65vh] overflow-y-auto pr-1">
        <form id={FORM_ID} onSubmit={methods.handleSubmit(onSubmit)}>
          <FormField
            control={methods.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.common.name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-16-bold capitalize text-fg-1">{t("model.propertySchema.modelNames")}</h2>
            <Button type="button" variant="outline" size="sm" onClick={schemas.append}>
              <Plus />
              <span>
                {isEdit
                  ? t("feature.dataImportProjects.form.addPropertySchema")
                  : t("ui.addX", { x: t("model.propertySchema.modelName") })}
              </span>
            </Button>
          </div>
          <PropertySchemaAssociationsList
            fields={schemas.fields}
            onRemove={schemas.remove}
            onMove={schemas.move}
            supportedPropertyTypes={SUPPORTED_PROPERTY_TYPES}
          />
        </div>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline" disabled={processing}>
            {t("ui.cancel")}
          </Button>
        </DialogClose>
        <Button type="submit" form={FORM_ID} disabled={processing}>
          {processing && <Loader2 className="animate-spin" />}
          <span>{t("ui.save")}</span>
        </Button>
      </DialogFooter>
    </FormProvider>
  );
}
